import React, { useEffect, useRef, useState } from 'react';

import { useIntl } from '../../../../util/reactIntl';
import { isAdmin } from '../../../../util/data';
import { ADMIN_LANDING_PAGE } from '../../../../util/adminPages';
import { getPendingListingsCount } from '../../../../util/api';
import { profilePageParams } from '../../../../util/userHelpers';

import { Avatar, NamedLink } from '../../../../components';

import IconChevronDown from '../icons/IconChevronDown';
import IconPlus from '../icons/IconPlus';
import { userMenuItems, userMenuLogout } from '../navigationConfig';

import css from './UserMenu.module.css';

const PLACEHOLDER_ROUTE_NAME = 'LandingPage';

/**
 * Avatar dropdown shown on the desktop topbar when the user is logged in.
 *
 * Click the trigger (avatar + chevron) to toggle the menu. The menu closes
 * on outside click and on Escape. Administrators get one extra entry above the
 * logout divider — "Admin", carrying the count of listings waiting for review —
 * which is hidden for everyone else, and the pages behind it redirect a
 * non-admin anyway. Items resolve to NamedLink destinations
 * defined in `navigationConfig.userMenuItems`; the logout item at the
 * bottom (below "Kontoinnstillinger") dispatches the parent's `onLogout`.
 * On mobile there's no dropdown — logout lives on the profile pages there.
 *
 * @param {Object} props
 * @param {Object} props.currentUser
 * @param {Function} props.onLogout
 * @returns {JSX.Element}
 */
const UserMenu = props => {
  const { currentUser, onLogout } = props;
  const intl = useIntl();

  const [isOpen, setIsOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(null);
  const containerRef = useRef(null);
  const showAdminItems = isAdmin(currentUser);

  // Asked each time an admin opens the menu rather than on every page load,
  // so nobody else's topbar ever makes the call. A failed count just leaves
  // the badge off.
  useEffect(() => {
    if (!isOpen || !showAdminItems) {
      return undefined;
    }
    let cancelled = false;
    getPendingListingsCount()
      .then(response => {
        if (!cancelled && typeof response?.count === 'number') {
          setPendingCount(response.count);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOpen, showAdminItems]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onClickOutside = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const onEscape = e => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [isOpen]);

  const handleLogout = () => {
    setIsOpen(false);
    onLogout?.();
  };

  return (
    <div className={css.root} ref={containerRef}>
      <button
        type="button"
        className={css.trigger}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(prev => !prev)}
      >
        <Avatar
          className={css.avatar}
          initialsClassName={css.avatarInitials}
          user={currentUser}
          disableProfileLink
        />
        <IconChevronDown className={css.chevron} />
      </button>

      {isOpen ? (
        <div className={css.menu} role="menu">
          <div className={css.menuItems}>
            {/* "Min profil" first, as in the phone menu (Guro, 9 October). */}
            {currentUser?.id ? (
              <NamedLink
                className={css.menuItem}
                name="ProfilePage"
                params={profilePageParams(currentUser)}
                role="menuitem"
                onClick={() => setIsOpen(false)}
              >
                {intl.formatMessage({ id: 'Topbar.userMenuViewProfile' })}
              </NamedLink>
            ) : null}
            <NamedLink
              className={css.createListingItem}
              name="NewListingPage"
              role="menuitem"
              onClick={() => setIsOpen(false)}
            >
              <IconPlus className={css.createListingIcon} />
              {intl.formatMessage({ id: 'Topbar.userMenuCreateListing' })}
            </NamedLink>
            {userMenuItems.map(item => {
              const label = intl.formatMessage({ id: item.labelMessageId });
              const routeName = item.placeholder ? PLACEHOLDER_ROUTE_NAME : item.name;
              return (
                <NamedLink
                  key={item.key}
                  className={css.menuItem}
                  name={routeName}
                  params={item.params}
                  role="menuitem"
                  onClick={() => setIsOpen(false)}
                >
                  {label}
                </NamedLink>
              );
            })}
            {showAdminItems ? (
              // One way in. The admin pages have their own nav along the top,
              // so listing all three here only made this menu longer. The
              // count rides along because it is the reason to go: it says
              // there is something waiting without having to look.
              <NamedLink
                className={css.adminItem}
                name={ADMIN_LANDING_PAGE}
                role="menuitem"
                onClick={() => setIsOpen(false)}
              >
                {intl.formatMessage({ id: 'Topbar.userMenuAdmin' })}
                {pendingCount > 0 ? (
                  <span
                    className={css.countBadge}
                    aria-label={intl.formatMessage(
                      { id: 'Topbar.userMenuAdminListingsCount' },
                      { count: pendingCount }
                    )}
                  >
                    {pendingCount}
                  </span>
                ) : null}
              </NamedLink>
            ) : null}
          </div>
          <div className={css.logoutWrapper}>
            <button
              type="button"
              className={css.logoutItem}
              role="menuitem"
              onClick={handleLogout}
            >
              {intl.formatMessage({ id: userMenuLogout.labelMessageId })}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default UserMenu;
